import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Language = "EN" | "ES" | "ZH" | "VI" | "FIL" | "KO" | "AR";
export type Need = "rent" | "food" | "shelter" | "jobs" | "legal";

interface LanguageState {
  selectedLanguage: Language;
  setLanguage: (language: Language) => Promise<void>;
  setSelectedLanguage: (language: Language) => void;
  error: string | null;
  isLoading: boolean;
}

const VALID_LANGUAGES: Language[] = ["EN", "ES", "ZH", "VI", "FIL", "KO", "AR"];

const isValidLanguage = (lang: string): lang is Language => {
  return VALID_LANGUAGES.includes(lang as Language);
};

const validateLanguage = (lang: string): { isValid: boolean; error?: string } => {
  if (!lang) {
    return { isValid: false, error: "Language code is required" };
  }
  if (!isValidLanguage(lang)) {
    return { 
      isValid: false, 
      error: `Invalid language code: ${lang}. Must be one of: ${VALID_LANGUAGES.join(", ")}` 
    };
  }
  return { isValid: true };
};

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      selectedLanguage: "EN",
      error: null,
      isLoading: false,
      setLanguage: async (language) => {
        if (get().selectedLanguage === language) {
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const validation = validateLanguage(language);
          if (!validation.isValid) {
            set({ error: validation.error || "Invalid language", isLoading: false });
            return;
          }

          await new Promise(resolve => setTimeout(resolve, 50));
          
          set({ 
            selectedLanguage: language as Language, 
            isLoading: false,
            error: null 
          });
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : "Failed to set language", 
            isLoading: false 
          });
        }
      },
      setSelectedLanguage: (language) => {
        const validation = validateLanguage(language);
        if (validation.isValid) {
          set({ selectedLanguage: language });
        }
      },
    }),
    {
      name: "language-storage",
      partialize: (state) => ({ selectedLanguage: state.selectedLanguage }),
      onRehydrateStorage: () => (state) => {
        if (state && typeof state.setLanguage === 'function') {
          const validation = validateLanguage(state.selectedLanguage);
          if (!validation.isValid) {
            state.setLanguage("EN");
          }
        }
      },
    }
  )
);

export const languageNames = {
  EN: "English",
  ES: "Español",
  ZH: "中文",
  VI: "Tiếng Việt",
  FIL: "Filipino",
  KO: "한국어",
  AR: "العربية",
};

export const translations = {
  selectNeed: {
    EN: "Select what you need help with",
    ES: "Selecciona con qué necesitas ayuda",
    ZH: "选择您需要帮助的内容",
    VI: "Chọn những gì bạn cần giúp đỡ",
    FIL: "Piliin ang kailangan mo ng tulong",
    KO: "도움이 필요한 항목을 선택하세요",
    AR: "حدد ما تحتاج المساعدة فيه",
  },
  common: {
    getHelp: {
      EN: "Get Help Now",
      ES: "Obtener Ayuda Ahora",
      ZH: "立即获取帮助",
      VI: "Nhận Trợ Giúp Ngay",
      FIL: "Kumuha ng Tulong Ngayon",
      KO: "지금 도움 받기",
      AR: "احصل على المساعدة الآن",
    },
    allowAccess: {
      EN: "Allow Access",
      ES: "Permitir Acceso",
      ZH: "允许访问",
      VI: "Cho Phép Truy Cập",
      FIL: "Payagan ang Access",
      KO: "접근 허용",
      AR: "السماح بالوصول",
    },
    aiVideoAdvisor: {
      EN: "AI Video Advisor",
      ES: "Asesor de Video IA",
      ZH: "AI视频顾问",
      VI: "Cố vấn Video AI",
      FIL: "Tagapayo sa Video AI",
      KO: "AI 비디오 상담사",
      AR: "مستشار الفيديو الذكي",
    },
    loadingVideoAvatar: {
      EN: "Loading video avatar",
      ES: "Cargando avatar de video",
      ZH: "加载视频头像",
      VI: "Đang tải avatar video",
      FIL: "Naglo-load ng video avatar",
      KO: "비디오 아바타 로딩 중",
      AR: "جاري تحميل الصورة الرمزية للفيديو",
    },
    demoNote: {
      EN: "Demo Note",
      ES: "Nota de Demostración",
      ZH: "演示说明",
      VI: "Ghi chú Demo",
      FIL: "Demo Note",
      KO: "데모 노트",
      AR: "ملاحظة تجريبية",
    },
    back: {
      EN: "Back",
      ES: "Atrás",
      ZH: "返回",
      VI: "Quay lại",
      FIL: "Bumalik",
      KO: "뒤로",
      AR: "رجوع",
    },
    backToDetails: {
      EN: "Back to Details",
      ES: "Volver a Detalles",
      ZH: "返回详情",
      VI: "Quay lại Chi Tiết",
      FIL: "Bumalik sa Detalye",
      KO: "상세 정보로 돌아가기",
      AR: "العودة إلى التفاصيل",
    },
    viewDetails: {
      EN: "View Details",
      ES: "Ver Detalles",
      ZH: "查看详情",
      VI: "Xem Chi Tiết",
      FIL: "Tingnan ang Detalye",
      KO: "상세 보기",
      AR: "عرض التفاصيل",
    },
    openNow: {
      EN: "Open Now",
      ES: "Abierto Ahora",
      ZH: "现在营业",
      VI: "Đang Mở",
      FIL: "Bukas Ngayon",
      KO: "영업 중",
      AR: "مفتوح الآن",
    },
    zipCode: {
      EN: "Zip Code",
      ES: "Código Postal",
      ZH: "邮政编码",
      VI: "Mã Bưu Điện",
      FIL: "Zip Code",
      KO: "우편번호",
      AR: "الرمز البريدي",
    },
    poweredByBolt: {
      EN: "Built by Bolt.new",
      ES: "Built by Bolt.new",
      ZH: "Built by Bolt.new",
      VI: "Built by Bolt.new",
      FIL: "Built by Bolt.new",
      KO: "Built by Bolt.new",
      AR: "Built by Bolt.new",
    },
  },
  home: {
    title: {
      EN: "Instant AI Aid When You Need It Most",
      ES: "Obtén Ayuda. Ahora Mismo.",
      ZH: "立即获取帮助。",
      VI: "Nhận Trợ Giúp. Ngay Bây Giờ.",
      FIL: "Kumuha ng Tulong. Ngayon Din.",
      KO: "지금 바로 도움을 받으세요.",
      AR: "احصل على المساعدة. الآن.",
    },
    subtitle: {
      EN: "Our emergency AI connects you to housing, food and legal help in seconds.",
      ES: "Acceso instantáneo a programas de ayuda de emergencia en tu área — para vivienda, comida, ayuda legal y más.",
      ZH: "立即获取您所在地区的紧急援助计划 — 住房、食品、法律援助等。",
      VI: "Truy cập ngay các chương trình hỗ trợ khẩn cấp trong khu vực của bạn — về nhà ở, thực phẩm, hỗ trợ pháp lý, và nhiều hơn nữa.",
      FIL: "Instant access sa mga programa ng emergency aid sa iyong lugar — para sa pabahay, pagkain, legal na tulong, at iba pa.",
      KO: "주거, 식품, 법률 지원 등 — 지역 내 긴급 지원 프로그램에 즉시 접근하세요.",
      AR: "وصول فوري إلى برامج المساعدات الطارئة في منطقتك — للسكن والغذاء والمساعدة القانونية والمزيد.",
    },
  },
  stepLabels: {
    selectNeed: {
      EN: "Select Need",
      ES: "Seleccionar Necesidad",
      ZH: "选择需求",
      VI: "Chọn Nhu Cầu",
      FIL: "Pumili ng Pangangailangan",
      KO: "필요 선택",
      AR: "اختر احتياجك",
    },
    matchSupport: {
      EN: "Match Support",
      ES: "Coincidencia",
      ZH: "匹配支持",
      VI: "Tìm Hỗ Trợ Phù Hợp",
      FIL: "Itugma ang Suporta",
      KO: "지원 매칭",
      AR: "مطابقة الدعم",
    },
    exploreAid: {
      EN: "Explore Aid",
      ES: "Explorar Ayuda",
      ZH: "探索援助",
      VI: "Khám Phá Hỗ Trợ",
      FIL: "Tuklasin ang Tulong",
      KO: "지원 탐색",
      AR: "استكشف المساعدة",
    },
  },
  needs: {
    rent: {
      EN: "Rent",
      ES: "Alquiler",
      ZH: "租金",
      VI: "Tiền Thuê Nhà",
      FIL: "Upa",
      KO: "임대료",
      AR: "الإيجار",
    },
    food: {
      EN: "Food",
      ES: "Comida",
      ZH: "食品",
      VI: "Thực Phẩm",
      FIL: "Pagkain",
      KO: "식품",
      AR: "الطعام",
    },
    shelter: {
      EN: "Shelter",
      ES: "Refugio",
      ZH: "住所",
      VI: "Nơi Trú Ẩn",
      FIL: "Tirahan",
      KO: "주거지",
      AR: "المأوى",
    },
    legal: {
      EN: "Legal Help",
      ES: "Ayuda Legal",
      ZH: "法律援助",
      VI: "Hỗ Trợ Pháp Lý",
      FIL: "Tulong Legal",
      KO: "법률 지원",
      AR: "المساعدة القانونية",
    },
    jobs: {
      EN: "Jobs",
      ES: "Empleos",
      ZH: "工作",
      VI: "Việc Làm",
      FIL: "Trabaho",
      KO: "취업",
      AR: "وظائف",
    },
  },
  match: {
    title: {
      EN: "Find Your Best Program Matches",
      ES: "Encuentra tus Mejores Coincidencias de Programas",
      ZH: "找到最适合您的项目",
      VI: "Tìm Chương Trình Phù Hợp Nhất",
      FIL: "Hanapin ang Pinakamahusay na Programa para sa Iyo",
      KO: "가장 적합한 프로그램 찾기",
      AR: "ابحث عن أفضل البرامج المناسبة لك",
    },
    subtitle: {
      EN: "Answer a few questions to help us find the most suitable aid programs for your situation.",
      ES: "Responde algunas preguntas para ayudarnos a encontrar los programas de ayuda más adecuados para tu situación.",
      ZH: "回答几个问题，帮助我们找到最适合您情况的援助计划。",
      VI: "Trả lời một vài câu hỏi để giúp chúng tôi tìm các chương trình hỗ trợ phù hợp nhất với tình hình của bạn.",
      FIL: "Sagutin ang ilang mga tanong upang matulungan kaming mahanap ang pinakangangkop na mga programa ng tulong para sa iyong sitwasyon.",
      KO: "몇 가지 질문에 답하시면 귀하의 상황에 가장 적합한 매칭 결과입니다. 각 프로그램은 지역 정보와 명확한 신청 단계를 포함합니다.",
      AR: "أجب على بعض الأسئلة لمساعدتنا في العثور على برامج المساعدة الأنسب لحالتك.",
    },
  },
  results: {
    title: {
      EN: "Your Program Matches",
      ES: "Tus Programas Coincidentes",
      ZH: "您的计划匹配",
      VI: "Các Chương trình Phù hợp của bạn",
      FIL: "Ang Iyong mga Pagtutugma ng Programa",
      KO: "귀하의 프로그램 매치",
      AR: "مطابقات برنامجك",
    },
    subtitle: {
      EN: "Based on your information, here are the programs that best match your needs.",
      ES: "Según tu información, aquí están los programas que mejor se adaptan a tus necesidades.",
      ZH: "根据您的信息，这里是与您的需求最匹配的计划。",
      VI: "Dựa trên thông tin của bạn, đây là các chương trình phù hợp nhất với nhu cầu của bạn.",
      FIL: "Batay sa iyong impormasyon, narito ang mga programa na pinakamahusay na tumutugma sa iyong mga pangangailangan.",
      KO: "귀하의 정보를 바탕으로, 귀하의 필요에 가장 잘 맞는 프로그램은 다음과 같습니다.",
      AR: "بناءً على معلوماتك، إليك البرامج التي تتوافق بشكل أفضل مع احتياجاتك.",
    },
    found: {
      EN: "Found {count} programs sorted by best match",
      ES: "Se encontraron {count} programas ordenados por mejor coincidencia",
      ZH: "找到 {count} 个按最佳匹配排序的计划",
      VI: "Tìm thấy {count} chương trình được sắp xếp theo mức độ phù hợp nhất",
      FIL: "Nahanap ang {count} mga programa na inayos ayon sa pinakamahusay na pagkakatugma",
      KO: "최고 매치 순으로 정렬된 {count}개의 프로그램을 찾았습니다",
      AR: "تم العثور على {count} من البرامج مرتبة حسب أفضل تطابق"
    },
    matchedPrograms: {
      EN: "Personalized Program Matches",
      ES: "Coincidencias de Programas Personalizadas",
      ZH: "个性化项目匹配",
      VI: "Các Chương Trình Phù Hợp Cá Nhân",
      FIL: "Mga Personalized na Programa",
      KO: "맞춤형 프로그램 매칭",
      AR: "برامج مخصصة تناسبك",
    },
    matchDescription: {
      EN: "Based on your answers, these are your strongest matches. Each program includes local information and clear steps to apply.",
      ES: "Según tus respuestas, estas son tus mejores coincidencias. Cada programa incluye información local y pasos claros para aplicar.",
      ZH: "根据您的回答，这些是最适合您的匹配。每个项目都包含本地信息和清晰的申请步骤。",
      VI: "Dựa trên câu trả lời của bạn, đây là những chương trình phù hợp nhất. Mỗi chương trình bao gồm thông tin địa phương và các bước đăng ký rõ ràng.",
      FIL: "Base sa iyong mga sagot, ito ang pinakamalakas na mga tugma. Bawat programa ay may lokal na impormasyon at malinaw na mga hakbang sa pag-apply.",
      KO: "귀하의 답변을 바탕으로 가장 적합한 매칭 결과입니다. 각 프로그램은 지역 정보와 명확한 신청 단계를 포함합니다.",
      AR: "بناءً على إجاباتك، هذه هي أفضل البرامج المناسبة لك. يتضمن كل برنامج معلومات محلية وخطوات واضحة للتقديم.",
    },
  },
  details: {
    readyToCall: {
      EN: "Ready to Call?",
      ES: "¿Listo para Llamar?",
      ZH: "准备好打电话了吗？",
      VI: "Sẵn Sàng Gọi?",
      FIL: "Handa na Bang Tumawag?",
      KO: "전화하실 준비가 되셨나요?",
      AR: "مستعد للاتصال؟",
    },
    callNow: {
      EN: "Call Now",
      ES: "Llamar Ahora",
      ZH: "立即致电",
      VI: "Gọi Ngay",
      FIL: "Tumawag Ngayon",
      KO: "지금 전화하기",
      AR: "اتصل الآن",
    },
  },
  appeal: {
    title: {
      EN: "Appeal Letter Preview",
      ES: "Vista Previa de Carta de Apelación",
      ZH: "申诉信预览",
      VI: "Xem Trước Thư Kháng Cáo",
      FIL: "Preview ng Appeal Letter",
      KO: "이의 제기 편지 미리보기",
      AR: "معاينة خطاب الاستئناف",
    },
    download: {
      EN: "Download Letter",
      ES: "Descargar Carta",
      ZH: "下载信件",
      VI: "Tải Xuống Thư",
      FIL: "I-download ang Liham",
      KO: "편지 다운로드",
      AR: "تحميل الخطاب",
    },
    send: {
      EN: "Send via Email",
      ES: "Enviar por Correo",
      ZH: "通过电子邮件发送",
      VI: "Gửi qua Email",
      FIL: "Ipadala sa Email",
      KO: "이메일로 보내기",
      AR: "إرسال عبر البريد الإلكتروني",
    },
  },
  appealLetter: {
    back: {
      EN: "Back",
      ES: "Atrás",
      ZH: "返回",
      VI: "Quay lại",
      FIL: "Bumalik",
      KO: "뒤로",
      AR: "رجوع",
    },
    letterPreview: {
      EN: "Appeal Letter Preview",
      ES: "Vista Previa de Carta de Apelación",
      ZH: "申诉信预览",
      VI: "Xem Trước Thư Kháng Cáo",
      FIL: "Preview ng Appeal Letter",
      KO: "이의 제기 편지 미리보기",
      AR: "معاينة خطاب الاستئناف",
    },
    letterDesc: {
      EN: "We've generated a letter based on your situation. You can copy, edit, or send this to your program contact.",
      ES: "Hemos generado una carta basada en tu situación. Puedes copiar, editar o enviar esto a tu contacto del programa.",
      ZH: "我们已根据您的情况生成了一封信。您可以复制、编辑或发送给您的项目联系人。",
      VI: "Chúng tôi đã tạo một lá thư dựa trên tình huống của bạn. Bạn có thể sao chép, chỉnh sửa hoặc gửi cho người liên hệ chương trình.",
      FIL: "Gumawa kami ng liham base sa iyong sitwasyon. Maaari mong kopyahin, i-edit, o ipadala ito sa iyong contact sa programa.",
      KO: "귀하의 상황에 맞는 편지를 생성했습니다. 프로그램 담당자에게 복사, 편집 또는 전송할 수 있습니다.",
      AR: "لقد أنشأنا خطاباً بناءً على وضعك. يمكنك نسخه أو تعديله أو إرساله إلى جهة الاتصال بالبرنامج.",
    },
    contactInfo: {
      EN: "Program Contact Information",
      ES: "Información de Contacto del Programa",
      ZH: "项目联系信息",
      VI: "Thông Tin Liên Hệ Chương Trình",
      FIL: "Impormasyon ng Contact sa Programa",
      KO: "프로그램 연락처 정보",
      AR: "معلومات الاتصال بالبرنامج",
    },
    importantNote: {
      EN: "Important Note",
      ES: "Nota Importante",
      ZH: "重要提示",
      VI: "Lưu Ý Quan Trọng",
      FIL: "Mahalagang Paalala",
      KO: "중요 안내",
      AR: "ملاحظة مهمة",
    },
    replaceText: {
      EN: "Replace all [HIGHLIGHTED] text with your personal information before sending.",
      ES: "Reemplaza todo el texto [RESALTADO] con tu información personal antes de enviar.",
      ZH: "发送前请用您的个人信息替换所有[高亮]文本。",
      VI: "Thay thế tất cả văn bản [ĐƯỢC ĐÁNH DẤU] bằng thông tin cá nhân của bạn trước khi gửi.",
      FIL: "Palitan ang lahat ng [HIGHLIGHTED] na teksto ng iyong personal na impormasyon bago ipadala.",
      KO: "보내기 전에 모든 [강조] 텍스트를 귀하의 개인 정보로 교체하세요.",
      AR: "استبدل كل النص [المظلل] بمعلوماتك الشخصية قبل الإرسال.",
    },
    letterSent: {
      EN: "Letter sent successfully. You've taken the first step toward aid.",
      ES: "Carta enviada exitosamente. Has dado el primer paso hacia la ayuda.",
      ZH: "信件发送成功。您已迈出获得援助的第一步。",
      VI: "Gửi thư thành công. Bạn đã thực hiện bước đầu tiên hướng tới sự hỗ trợ.",
      FIL: "Matagumpay na naipadala ang liham. Ginawa mo na ang unang hakbang patungo sa tulong.",
      KO: "편지가 성공적으로 전송되었습니다. 지원을 향한 첫 걸음을 내디뎠습니다.",
      AR: "تم إرسال الخطاب بنجاح. لقد اتخذت الخطوة الأولى نحو المساعدة.",
    },
    downloaded: {
      EN: "Downloaded!",
      ES: "¡Descargado!",
      ZH: "已下载！",
      VI: "Đã tải xuống!",
      FIL: "Na-download na!",
      KO: "다운로드 완료!",
      AR: "تم التحميل!",
    },
    downloadLetter: {
      EN: "Download Letter",
      ES: "Descargar Carta",
      ZH: "下载信件",
      VI: "Tải Xuống Thư",
      FIL: "I-download ang Liham",
      KO: "편지 다운로드",
      AR: "تحميل الخطاب",
    },
    emailOpened: {
      EN: "Email Opened!",
      ES: "¡Correo Abierto!",
      ZH: "邮件已打开！",
      VI: "Đã Mở Email!",
      FIL: "Nabuksan ang Email!",
      KO: "이메일 열림!",
      AR: "تم فتح البريد الإلكتروني!",
    },
    sendEmail: {
      EN: "Send via Email",
      ES: "Enviar por Correo",
      ZH: "通过电子邮件发送",
      VI: "Gửi qua Email",
      FIL: "Ipadala sa Email",
      KO: "이메일로 보내기",
      AR: "إرسال عبر البريد الإلكتروني",
    },
    whatNext: {
      EN: "What Happens Next?",
      ES: "¿Qué Sucede Después?",
      ZH: "接下来会发生什么？",
      VI: "Điều Gì Xảy Ra Tiếp Theo?",
      FIL: "Ano ang Susunod na Mangyayari?",
      KO: "다음은 무엇인가요?",
      AR: "ماذا يحدث بعد ذلك؟",
    },
    letterReview: {
      EN: "Your letter will be sent to the program administrator for review",
      ES: "Tu carta será enviada al administrador del programa para revisión",
      ZH: "您的信件将发送给项目管理员审核",
      VI: "Thư của bạn sẽ được gửi đến quản trị viên chương trình để xem xét",
      FIL: "Ipapadala ang iyong liham sa administrator ng programa para sa review",
      KO: "귀하의 편지가 프로그램 관리자에게 검토를 위해 전송됩니다",
      AR: "سيتم إرسال خطابك إلى مدير البرنامج للمراجعة",
    },
    expectResponse: {
      EN: "Expect a response within 2-3 business days",
      ES: "Espera una respuesta dentro de 2-3 días hábiles",
      ZH: "预计在2-3个工作日内收到回复",
      VI: "Chờ phản hồi trong vòng 2-3 ngày làm việc",
      FIL: "Asahan ang sagot sa loob ng 2-3 araw ng trabaho",
      KO: "2-3 영업일 내에 응답을 기대하세요",
      AR: "توقع رداً خلال 2-3 أيام عمل",
    },
    haveDocuments: {
      EN: "Have your documentation ready if requested",
      ES: "Ten tu documentación lista si es solicitada",
      ZH: "如有要求，请准备好您的文件",
      VI: "Chuẩn bị sẵn tài liệu của bạn nếu được yêu cầu",
      FIL: "Ihanda ang iyong dokumentasyon kung hihilingin",
      KO: "요청 시 문서를 준비하세요",
      AR: "جهز مستنداتك إذا طُلبت",
    },
  },
  placeholders: {
    yourName: {
      EN: "[YOUR NAME]",
      ES: "[SU NOMBRE]",
      ZH: "[您的姓名]",
      VI: "[TÊN CỦA BẠN]",
      FIL: "[PANGALAN MO]",
      KO: "[귀하의 이름]",
      AR: "[اسمك]",
    },
    phoneEmail: {
      EN: "[PHONE/EMAIL]",
      ES: "[TELÉFONO/CORREO]",
      ZH: "[电话/邮箱]",
      VI: "[ĐIỆN THOẠI/EMAIL]",
      FIL: "[TELEPONO/EMAIL]",
      KO: "[전화/이메일]",
      AR: "[الهاتف/البريد الإلكتروني]",
    },
    zipCode: {
      EN: "[ZIP CODE]",
      ES: "[CÓDIGO POSTAL]",
      ZH: "[邮政编码]",
      VI: "[MÃ BƯU ĐIỆN]",
      FIL: "[ZIP CODE]",
      KO: "[우편번호]",
      AR: "[الرمز البريدي]",
    },
  },
  settings: {
    title: {
      EN: "Settings",
      ES: "Configuración",
      ZH: "设置",
      VI: "Cài đặt",
      FIL: "Mga Setting",
      KO: "설정",
      AR: "الإعدادات",
    },
    tabs: {
      accessibility: {
        EN: "Accessibility",
        ES: "Accesibilidad",
        ZH: "无障碍",
        VI: "Trợ năng",
        FIL: "Accessibility",
        KO: "접근성",
        AR: "إمكانية الوصول",
      },
      audio: {
        EN: "Audio & Voice",
        ES: "Audio y Voz",
        ZH: "音频和语音",
        VI: "Âm thanh & Giọng nói",
        FIL: "Audio at Tinig",
        KO: "오디오 및 음성",
        AR: "الصوت والكلام",
      },
      interface: {
        EN: "Interface",
        ES: "Interfaz",
        ZH: "界面",
        VI: "Giao diện",
        FIL: "Interface",
        KO: "인터페이스",
        AR: "الواجهة",
      },
      privacy: {
        EN: "Privacy",
        ES: "Privacidad",
        ZH: "隐私",
        VI: "Quyền riêng tư",
        FIL: "Privacy",
        KO: "개인정보",
        AR: "الخصوصية",
      },
      features: {
        EN: "Features",
        ES: "Funciones",
        ZH: "功能",
        VI: "Tính năng",
        FIL: "Mga Feature",
        KO: "기능",
        AR: "الميزات",
      },
      account: {
        EN: "Account",
        ES: "Cuenta",
        ZH: "账户",
        VI: "Tài khoản",
        FIL: "Account",
        KO: "계정",
        AR: "الحساب",
      },
    },
    accessibility: {
      highContrast: {
        EN: "High Contrast Mode",
        ES: "Modo de Alto Contraste",
        ZH: "高对比度模式",
        VI: "Chế độ Tương phản Cao",
        FIL: "High Contrast Mode",
        KO: "고대비 모드",
        AR: "وضع التباين العالي",
      },
      reducedMotion: {
        EN: "Reduce Motion",
        ES: "Reducir Movimiento",
        ZH: "减少动画",
        VI: "Giảm Chuyển động",
        FIL: "Reduce Motion",
        KO: "모션 줄이기",
        AR: "تقليل الحركة",
      },
      fontSize: {
        EN: "Text Size",
        ES: "Tamaño de Texto",
        ZH: "文字大小",
        VI: "Kích thước Chữ",
        FIL: "Laki ng Text",
        KO: "텍스트 크기",
        AR: "حجم النص",
      },
      screenReader: {
        EN: "Screen Reader Support",
        ES: "Soporte para Lector de Pantalla",
        ZH: "屏幕阅读器支持",
        VI: "Hỗ trợ Trình đọc Màn hình",
        FIL: "Screen Reader Support",
        KO: "스크린 리더 지원",
        AR: "دعم قارئ الشاشة",
      },
    },
    audio: {
      voiceEnabled: {
        EN: "Voice Input",
        ES: "Entrada de Voz",
        ZH: "语音输入",
        VI: "Nhập bằng Giọng nói",
        FIL: "Voice Input",
        KO: "음성 입력",
        AR: "الإدخال الصوتي",
      },
      ttsEnabled: {
        EN: "Text-to-Speech",
        ES: "Texto a Voz",
        ZH: "文本转语音",
        VI: "Chuyển văn bản thành giọng nói",
        FIL: "Text-to-Speech",
        KO: "텍스트 음성 변환",
        AR: "تحويل النص إلى كلام",
      },
      autoSpeak: {
        EN: "Auto-speak Results",
        ES: "Lectura Automática de Resultados",
        ZH: "自动朗读结果",
        VI: "Tự động đọc Kết quả",
        FIL: "Auto-speak Results",
        KO: "결과 자동 읽기",
        AR: "قراءة النتائج تلقائياً",
      },
    },
    interface: {
      theme: {
        EN: "Theme",
        ES: "Tema",
        ZH: "主题",
        VI: "Chủ đề",
        FIL: "Theme",
        KO: "테마",
        AR: "السمة",
      },
      language: {
        EN: "Language",
        ES: "Idioma",
        ZH: "语言",
        VI: "Ngôn ngữ",
        FIL: "Wika",
        KO: "언어",
        AR: "اللغة",
      },
      compactMode: {
        EN: "Compact Mode",
        ES: "Modo Compacto",
        ZH: "紧凑模式",
        VI: "Chế độ Gọn",
        FIL: "Compact Mode",
        KO: "컴팩트 모드",
        AR: "الوضع المضغوط",
      },
      animations: {
        EN: "Animations",
        ES: "Animaciones",
        ZH: "动画",
        VI: "Hoạt ảnh",
        FIL: "Animations",
        KO: "애니메이션",
        AR: "الرسوم المتحركة",
      },
    },
    privacy: {
      analytics: {
        EN: "Analytics",
        ES: "Analíticas",
        ZH: "分析",
        VI: "Phân tích",
        FIL: "Analytics",
        KO: "분석",
        AR: "التحليلات",
      },
      performance: {
        EN: "Performance Monitoring",
        ES: "Monitoreo de Rendimiento",
        ZH: "性能监控",
        VI: "Giám sát Hiệu suất",
        FIL: "Performance Monitoring",
        KO: "성능 모니터링",
        AR: "مراقبة الأداء",
      },
      crashReporting: {
        EN: "Crash Reporting",
        ES: "Reporte de Errores",
        ZH: "崩溃报告",
        VI: "Báo cáo Lỗi",
        FIL: "Crash Reporting",
        KO: "충돌 보고",
        AR: "تقارير الأعطال",
      },
    },
    common: {
      save: {
        EN: "Save",
        ES: "Guardar",
        ZH: "保存",
        VI: "Lưu",
        FIL: "I-save",
        KO: "저장",
        AR: "حفظ",
      },
      cancel: {
        EN: "Cancel",
        ES: "Cancelar",
        ZH: "取消",
        VI: "Hủy",
        FIL: "Cancel",
        KO: "취소",
        AR: "إلغاء",
      },
      reset: {
        EN: "Reset to Defaults",
        ES: "Restablecer por Defecto",
        ZH: "重置为默认",
        VI: "Đặt lại Mặc định",
        FIL: "Reset to Defaults",
        KO: "기본값으로 재설정",
        AR: "إعادة تعيين إلى الافتراضي",
      },
    },
  },
  onboarding: {
    welcome: {
      title: {
        EN: "Welcome to RightNowAid",
        ES: "Bienvenido a RightNowAid",
        ZH: "欢迎使用RightNowAid",
        VI: "Chào mừng đến với RightNowAid",
        FIL: "Maligayang pagdating sa RightNowAid",
        KO: "RightNowAid에 오신 것을 환영합니다",
        AR: "مرحباً بك في RightNowAid",
      },
      subtitle: {
        EN: "Let's personalize your experience",
        ES: "Personalicemos tu experiencia",
        ZH: "让我们个性化您的体验",
        VI: "Hãy cá nhân hóa trải nghiệm của bạn",
        FIL: "I-personalize natin ang inyong experience",
        KO: "경험을 개인화해보세요",
        AR: "دعنا نخصص تجربتك",
      },
      description: {
        EN: "We'll help you set up accessibility features and preferences to make RightNowAid work best for you.",
        ES: "Te ayudaremos a configurar funciones de accesibilidad y preferencias para que RightNowAid funcione mejor para ti.",
        ZH: "我们将帮助您设置无障碍功能和偏好，让RightNowAid最适合您。",
        VI: "Chúng tôi sẽ giúp bạn thiết lập các tính năng trợ năng và tùy chọn để RightNowAid hoạt động tốt nhất cho bạn.",
        FIL: "Tutulungan ka namin na mag-setup ng accessibility features at preferences para sa RightNowAid na magtatrabaho ng pinakamabuti para sa iyo.",
        KO: "RightNowAid가 귀하에게 가장 적합하도록 접근성 기능과 환경설정을 설정하도록 도와드리겠습니다.",
        AR: "سنساعدك في إعداد ميزات الوصول والتفضيلات لجعل RightNowAid يعمل بأفضل شكل لك.",
      },
    },
    steps: {
      next: {
        EN: "Next",
        ES: "Siguiente",
        ZH: "下一步",
        VI: "Tiếp theo",
        FIL: "Susunod",
        KO: "다음",
        AR: "التالي",
      },
      skip: {
        EN: "Skip",
        ES: "Omitir",
        ZH: "跳过",
        VI: "Bỏ qua",
        FIL: "Laktawan",
        KO: "건너뛰기",
        AR: "تخطي",
      },
      finish: {
        EN: "Finish",
        ES: "Finalizar",
        ZH: "完成",
        VI: "Hoàn thành",
        FIL: "Tapos na",
        KO: "완료",
        AR: "إنهاء",
      },
    },
  },
};

// Helper functions for language-related operations
export const getLanguageName = (code: Language): string => {
  const names: Record<Language, string> = {
    EN: "English",
    ES: "Español",
    ZH: "中文",
    VI: "Tiếng Việt",
    FIL: "Filipino",
    KO: "한국어",
    AR: "العربية",
  };
  return names[code] || code;
};

export const getLanguageDirection = (code: Language): "ltr" | "rtl" => {
  return code === "AR" ? "rtl" : "ltr";
};

export const getLanguageLocale = (code: Language): string => {
  const locales: Record<Language, string> = {
    EN: "en-US",
    ES: "es-ES",
    ZH: "zh-CN",
    VI: "vi-VN",
    FIL: "fil-PH",
    KO: "ko-KR",
    AR: "ar-SA",
  };
  return locales[code] || "en-US";
};
